/**
 * persistence/vcs/yjsDoc.js
 *
 * Yjs document lifecycle + the string ↔ Y.Array bridge primitives.
 *
 * The analyst's work is a comma-separated, append-mostly list of event tokens
 * (e.g. "P-A-B-1H-1-…,S-A-B-1H-2-…").  That maps one-to-one onto an ordered
 * CRDT sequence, so we model it as a Y.Array<string> named "events" inside a
 * Y.Doc.  Yjs then gives us conflict-free convergence for free, which the
 * BranchManager / MergeManager build the git-style UX on top of.
 *
 * On-the-wire fidelity:
 *   We store ONLY meaningful tokens (what parseEventTokens keeps — i.e. the
 *   sentinel EMPTY_EVENT_TOKEN and blank tokens are dropped).  Reconstructing a
 *   string is therefore `tokens.join(",")`.  This matches how the rest of the
 *   app reasons about events (PreviewDialog, DiffManager, countEvents all parse
 *   the same way), so the round-trip is stable for version-control purposes.
 *
 * Storage:
 *   Locally (Dexie) we persist the binary update blob (Uint8Array) directly —
 *   IndexedDB stores typed arrays natively via structured clone.
 *   For Firestore we base64-encode the same bytes (Firestore has no binary
 *   array type that round-trips cleanly through the web SDK).
 */

import * as Y from "yjs";
import { parseEventTokens } from "../managers/DiffManager";

/** The Y.Array key that holds the ordered event tokens. */
export const EVENTS_KEY = "events";

// ─── Doc lifecycle ──────────────────────────────────────────────────────────

/**
 * Creates a fresh, empty Y.Doc.
 * @returns {Y.Doc}
 */
export function createDoc() {
  return new Y.Doc();
}

/**
 * Builds a Y.Doc from a stored binary update blob.
 * Tolerates null/empty input → returns an empty doc.
 *
 * @param {Uint8Array|null|undefined} stateUpdate
 * @returns {Y.Doc}
 */
export function loadDoc(stateUpdate) {
  const doc = new Y.Doc();
  if (stateUpdate && stateUpdate.byteLength > 0) {
    Y.applyUpdate(doc, toUint8(stateUpdate));
  }
  return doc;
}

/**
 * Encodes the full state of a doc as a single update blob (Uint8Array).
 * Suitable for storing as a branch HEAD or a commit snapshot.
 *
 * @param {Y.Doc} doc
 * @returns {Uint8Array}
 */
export function encodeDoc(doc) {
  return Y.encodeStateAsUpdate(doc);
}

/**
 * Returns the shared events array for a doc, creating it if absent.
 * @param {Y.Doc} doc
 * @returns {Y.Array<string>}
 */
export function getEventsArray(doc) {
  return doc.getArray(EVENTS_KEY);
}

/**
 * Merges another doc's encoded state into `doc` (CRDT union — conflict-free,
 * idempotent: applying the same update twice is a no-op).  Tolerates null /
 * empty updates.  Used to fold a cloud branch state into the local one.
 *
 * @param {Y.Doc} doc
 * @param {Uint8Array|null|undefined} stateUpdate
 */
export function mergeStateInto(doc, stateUpdate) {
  if (stateUpdate && stateUpdate.byteLength > 0) {
    Y.applyUpdate(doc, toUint8(stateUpdate));
  }
}

/**
 * True when two encoded states share at least one Yjs client — i.e. one was
 * built from the other (directly or via a merge).  Merging states that share
 * nothing does not dedupe: both event lists end up concatenated.
 *
 * @param {Uint8Array} a
 * @param {Uint8Array} b
 * @returns {boolean}
 */
export function sharesHistory(a, b) {
  const clientsA = Y.decodeStateVector(Y.encodeStateVectorFromUpdate(toUint8(a)));
  const clientsB = Y.decodeStateVector(Y.encodeStateVectorFromUpdate(toUint8(b)));
  for (const client of clientsA.keys()) {
    if (clientsB.has(client)) return true;
  }
  return false;
}

// ─── Token ↔ string bridge ────────────────────────────────────────────────────

/**
 * Splits a raw event string into meaningful tokens.
 * Delegates to the canonical parser so the VCS layer and the diff/checkpoint
 * layers always agree on what counts as an event.
 *
 * @param {string} rawString
 * @returns {string[]}
 */
export function stringToTokens(rawString) {
  return parseEventTokens(rawString);
}

/**
 * Joins tokens back into the comma-separated string the editor / API expect.
 * @param {string[]} tokens
 * @returns {string}
 */
export function tokensToString(tokens) {
  return (tokens ?? []).join(",");
}

/**
 * Current event tokens held in a doc's events array.
 * @param {Y.Doc} doc
 * @returns {string[]}
 */
export function docToTokens(doc) {
  return getEventsArray(doc).toArray();
}

/**
 * Current event string represented by a doc.
 * @param {Y.Doc} doc
 * @returns {string}
 */
export function docToString(doc) {
  return tokensToString(docToTokens(doc));
}

/**
 * Reconciles a doc's events array to equal `nextTokens` using a minimal
 * "trim equal ends" patch — delete the changed middle, insert the new middle.
 *
 * Trimming the common prefix/suffix preserves the CRDT identity of unchanged
 * head/tail tokens, which is what lets divergent branches merge cleanly.  For
 * the common append case this collapses to a single insert at the end.
 *
 * @param {Y.Doc} doc
 * @param {string[]} nextTokens
 * @returns {boolean} true if the array actually changed
 */
export function setDocTokens(doc, nextTokens) {
  const yarr = getEventsArray(doc);
  const cur = yarr.toArray();

  let p = 0;
  while (p < cur.length && p < nextTokens.length && cur[p] === nextTokens[p]) p++;

  let s = 0;
  while (
    s < cur.length - p &&
    s < nextTokens.length - p &&
    cur[cur.length - 1 - s] === nextTokens[nextTokens.length - 1 - s]
  ) {
    s++;
  }

  const delCount = cur.length - p - s;
  const insTokens = nextTokens.slice(p, nextTokens.length - s);

  if (delCount === 0 && insTokens.length === 0) return false;

  doc.transact(() => {
    if (delCount > 0) yarr.delete(p, delCount);
    if (insTokens.length > 0) yarr.insert(p, insTokens);
  });
  return true;
}

// ─── Base64 (Firestore transport) ──────────────────────────────────────────────

/**
 * Encodes a binary update blob as a base64 string for Firestore storage.
 * @param {Uint8Array} bytes
 * @returns {string}
 */
export function encodeUpdateBase64(bytes) {
  const arr = toUint8(bytes);
  let binary = "";
  const chunk = 0x8000; // avoid call-stack limits on String.fromCharCode.apply
  for (let i = 0; i < arr.length; i += chunk) {
    binary += String.fromCharCode.apply(null, arr.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Decodes a base64 string (from Firestore) back into a Uint8Array update blob.
 * @param {string} b64
 * @returns {Uint8Array}
 */
export function decodeUpdateBase64(b64) {
  const binary = atob(b64 ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ─── Internal ─────────────────────────────────────────────────────────────────

/**
 * Normalises stored blob shapes (Uint8Array, ArrayBuffer, or plain array from
 * a structured-clone round-trip) into a Uint8Array.
 */
function toUint8(blob) {
  if (blob instanceof Uint8Array) return blob;
  if (blob instanceof ArrayBuffer) return new Uint8Array(blob);
  return new Uint8Array(blob);
}
